import React, { useState } from "react";

/**
 * Terouma brand mark + wordmark — works on light AND dark backgrounds.
 *
 * The real logo is a navy drop, so on a navy background it would
 * disappear into the surface. Pass ``onDark`` and the mark is rendered
 * inside a cream disc that delineates it clearly — the disc is sized to
 * fit snugly without any clipping.
 *
 * Props
 * -----
 * - `size`      — square px size of the mark (default 32).
 * - `showText`  — render the wordmark next to the mark (default true).
 * - `textColor` — wordmark colour. Default cream for `onDark`, navy otherwise.
 * - `variant`
 *     "full"     — mark + "TEROUMA" + Hebrew subtitle (default)
 *     "wordmark" — mark + "TEROUMA" (no Hebrew subtitle)
 *     "icon"     — mark alone, no text.
 * - `onDark`    — wrap the mark in a cream disc. Use on navy/dark BGs.
 */

// Module-level memo so we don't re-probe /logo.png on every component.
let _logoPngMissing = false;

export default function TeroumaLogo({
  size = 32,
  showText = true,
  textColor,
  variant = "full",
  onDark = false,
}) {
  const [imgFailed, setImgFailed] = useState(_logoPngMissing);

  // Sensible defaults: cream wordmark on dark, navy wordmark on light.
  const resolvedTextColor = textColor ?? (onDark ? "#F4EFE2" : "#0D1B33");

  // The mark itself — either a PNG or the inline SVG fallback.
  const PlainMark = imgFailed ? (
    <InlineMark size={size} />
  ) : (
    <img
      src="/logo.png"
      alt=""
      height={size}
      onError={() => {
        _logoPngMissing = true;
        setImgFailed(true);
      }}
      style={{
        height: `${size}px`,
        width: "auto",
        display: "block",
        flexShrink: 0,
      }}
    />
  );

  // Wrap in a cream disc when displayed on dark surfaces.
  // Disc size is just a hair bigger than the mark height to avoid the
  // disc being clipped by tight container paddings.
  const Mark = onDark ? (
    <span
      className="shrink-0 inline-flex items-center justify-center rounded-full"
      style={{
        // Disc slightly larger than the mark so there's a small cream halo.
        // Stays close to the mark height so the disc never overflows
        // narrow containers (sidebar collapse, etc.).
        height: `${Math.round(size * 1.08)}px`,
        width: `${Math.round(size * 1.08)}px`,
        background: "#FAF7F0",
        boxShadow:
          "0 0 0 1px rgba(184,150,46,0.28), 0 2px 8px rgba(0,0,0,0.25)",
      }}
    >
      <span
        style={{
          display: "inline-flex",
          height: `${Math.round(size * 0.85)}px`,
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {imgFailed ? (
          <InlineMark size={Math.round(size * 0.85)} />
        ) : (
          <img
            src="/logo.png"
            alt=""
            height={Math.round(size * 0.85)}
            onError={() => {
              _logoPngMissing = true;
              setImgFailed(true);
            }}
            style={{
              height: `${Math.round(size * 0.85)}px`,
              width: "auto",
              display: "block",
            }}
          />
        )}
      </span>
    </span>
  ) : (
    PlainMark
  );

  if (!showText || variant === "icon") {
    return <span style={{ display: "inline-flex", alignItems: "center" }}>{Mark}</span>;
  }

  // Wordmark sizing tuned so the drop logo reads as the dominant element
  // (we tried 0.78 — too heavy; 0.55 — too light; 0.6 hits the sweet spot).
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: `${Math.max(10, size * 0.32)}px`,
        lineHeight: 1,
      }}
    >
      {Mark}
      <span
        style={{
          display: "inline-flex",
          flexDirection: "column",
          gap: `${Math.max(2, size * 0.04)}px`,
          lineHeight: 1,
        }}
      >
        <span
          style={{
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            fontSize: `${size * 0.6}px`,
            fontWeight: 600,
            color: resolvedTextColor,
            letterSpacing: "0.14em",
          }}
        >
          TEROUMA
        </span>
        {variant === "full" && (
          <span
            style={{
              fontSize: `${Math.max(9, size * 0.24)}px`,
              color: "#C9A84C",
              letterSpacing: "0.22em",
              fontFamily: "'Manrope', 'Inter', sans-serif",
              fontWeight: 500,
            }}
          >
            תרומה
          </span>
        )}
      </span>
    </span>
  );
}

/**
 * InlineMark — fallback SVG when /logo.png is unavailable.
 * Aspect ratio matched to the real logo (≈0.63 wide : 1 tall).
 */
function InlineMark({ size = 32 }) {
  const width = Math.round(size * 0.63);
  return (
    <svg
      width={width}
      height={size}
      viewBox="0 0 100 158"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Terouma"
      style={{ display: "block", flexShrink: 0 }}
    >
      <defs>
        <linearGradient id="trmGold" x1="0" y1="0" x2="100" y2="100" gradientUnits="userSpaceOnUse">
          <stop stopColor="#E2C56C" />
          <stop offset="1" stopColor="#B8962E" />
        </linearGradient>
        <linearGradient id="trmNavy" x1="20" y1="0" x2="80" y2="110" gradientUnits="userSpaceOnUse">
          <stop stopColor="#16284A" />
          <stop offset="1" stopColor="#0A1428" />
        </linearGradient>
      </defs>
      <path
        d="M50 4 C 30 32, 14 56, 14 78 C 14 96, 30 107, 50 107 C 70 107, 86 96, 86 78 C 86 56, 70 32, 50 4 Z"
        fill="url(#trmNavy)"
      />
      <path
        d="M58 8 C 70 28, 84 50, 88 72 C 90 84, 86 95, 78 102"
        stroke="url(#trmGold)"
        strokeWidth="7"
        strokeLinecap="round"
        fill="none"
      />
      <path
        d="M28 52 H72 M30 52 V82 C 30 86, 33 88, 38 88 M70 52 V70 C 70 78, 64 84, 56 84 M56 84 C 60 88, 64 90, 68 90"
        stroke="url(#trmGold)"
        strokeWidth="6"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

export { InlineMark };
